import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ThreeDotsVertical } from 'react-bootstrap-icons'

const MENU_Z_INDEX = 10050

export default function PortalKebabMenu({
  items,
  ariaLabel = 'Open menu',
  buttonClassName = 'dark:text-white p-1 rounded hover:bg-gray-100 dark:hover:bg-rb-dark-mode-lighter',
}) {
  const anchorRef = useRef(null)
  const menuRef = useRef(null)
  const [isOpen, setIsOpen] = useState(false)
  const [menuPosition, setMenuPosition] = useState(null)

  const close = useCallback(() => setIsOpen(false), [])

  const updateMenuPosition = useCallback(() => {
    const anchor = anchorRef.current
    if (!anchor) return

    const rect = anchor.getBoundingClientRect()
    const menuEl = menuRef.current
    const menuHeight = menuEl?.offsetHeight ?? 72
    const menuWidth = menuEl?.offsetWidth ?? 144
    const gap = 4
    const pad = 8

    let top = rect.bottom + gap
    if (top + menuHeight > window.innerHeight - pad) {
      top = rect.top - gap - menuHeight
    }
    top = Math.max(pad, Math.min(top, window.innerHeight - menuHeight - pad))

    let left = rect.right - menuWidth
    left = Math.max(pad, Math.min(left, window.innerWidth - menuWidth - pad))

    setMenuPosition({ top, left })
  }, [])

  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuPosition(null)
      return undefined
    }

    const raf = requestAnimationFrame(() => updateMenuPosition())
    const onLayout = () => updateMenuPosition()
    window.addEventListener('resize', onLayout)
    window.addEventListener('scroll', onLayout, true)

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', onLayout)
      window.removeEventListener('scroll', onLayout, true)
    }
  }, [isOpen, updateMenuPosition])

  useEffect(() => {
    if (!isOpen) return undefined

    const handleOutsideClick = (event) => {
      if (anchorRef.current?.contains(event.target)) return
      if (menuRef.current?.contains(event.target)) return
      close()
    }
    const handleEscape = (event) => {
      if (event.key === 'Escape') close()
    }

    document.addEventListener('mousedown', handleOutsideClick)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, close])

  const menu =
    isOpen && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={menuRef}
            role="menu"
            className="w-36 rounded-md bg-white dark:bg-rb-dark-mode border-1 border-rb-widget-border dark:border-rb-dark-mode-lighter shadow-md py-1"
            style={{
              position: 'fixed',
              top: menuPosition?.top ?? -9999,
              left: menuPosition?.left ?? -9999,
              zIndex: MENU_Z_INDEX,
              visibility: menuPosition ? 'visible' : 'hidden',
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {items.map((item) => (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                disabled={item.disabled}
                title={item.title}
                className={
                  item.className ||
                  `p-2 w-full text-left flex items-center gap-2 dark:text-white text-sm ${
                    item.disabled
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:bg-gray-100 dark:hover:bg-rb-dark-mode-lighter'
                  }`
                }
                onClick={(e) => {
                  e.stopPropagation()
                  if (item.disabled) return
                  close()
                  item.onClick?.(e)
                }}
              >
                {item.icon}
                {item.label}
              </button>
            ))}
          </div>,
          document.body,
        )
      : null

  return (
    <>
      <div ref={anchorRef}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setIsOpen((prev) => !prev)
          }}
          className={buttonClassName}
          aria-label={ariaLabel}
          aria-expanded={isOpen}
          aria-haspopup="menu"
        >
          <ThreeDotsVertical />
        </button>
      </div>
      {menu}
    </>
  )
}
